import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Minus, Plus, ShieldCheck, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { fetchPackage, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Eyebrow } from "@/components/Reveal";
import { PackageCard } from "@/components/PackageCard";

export const DettaglioPacchetto = () => {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [others, setOthers] = useState([]);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    setPkg(null);
    setQty(1);
    fetchPackage(slug).then(setPkg).catch(() => setPkg("404"));
    import("@/lib/api").then(({ fetchPackages }) =>
      fetchPackages().then(setOthers).catch(() => {})
    );
  }, [slug]);

  if (pkg === "404") {
    return (
      <div className="pt-40 pb-32 text-center">
        <h1 className="font-display text-4xl font-bold text-white">Pacchetto non trovato</h1>
        <Link to="/pacchetti" data-testid="detail-back-catalog" className="mt-6 inline-block text-electric underline">
          Torna al catalogo
        </Link>
      </div>
    );
  }

  if (!pkg) {
    return <div className="min-h-screen pt-40 flex justify-center"><div className="h-64 w-full max-w-7xl animate-pulse rounded-2xl bg-panel" /></div>;
  }

  const related = others.filter((o) => o.slug !== slug).slice(0, 3);

  const handleAdd = () => {
    addItem(pkg, qty);
    toast.success("Aggiunto al carrello", { description: `${pkg.title} × ${qty}` });
  };

  return (
    <div className="pt-24 lg:pt-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/pacchetti"
          data-testid="detail-back-link"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:text-electric"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Tutti i pacchetti
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-[1.15fr_1fr]">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-3xl border border-lineblue"
          >
            <img src={pkg.image} alt={pkg.title} className="aspect-[4/3] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
            <span className="absolute left-5 top-5 rounded-full bg-electric px-4 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-ink">
              {pkg.tag}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col"
          >
            <Eyebrow>{pkg.category}</Eyebrow>
            <h1 data-testid="detail-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
              {pkg.title}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-300">{pkg.short_desc}</p>

            <div className="mt-8 flex items-end gap-3 border-y border-lineblue py-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 self-center">Investimento</span>
              <span data-testid="detail-price" className="font-display text-4xl font-bold text-white">
                {formatPrice(pkg.price)}
              </span>
              <span className="pb-1 text-xs text-slate-500">netto / progetto</span>
            </div>

            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center rounded-full border border-lineblue">
                <button
                  data-testid="detail-qty-dec"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="flex h-12 w-12 items-center justify-center rounded-l-full text-slate-300 transition-colors hover:text-electric"
                  aria-label="Diminuisci quantità"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span data-testid="detail-qty-value" className="w-10 text-center font-display font-bold text-white">
                  {qty}
                </span>
                <button
                  data-testid="detail-qty-inc"
                  onClick={() => setQty((q) => Math.min(20, q + 1))}
                  className="flex h-12 w-12 items-center justify-center rounded-r-full text-slate-300 transition-colors hover:text-electric"
                  aria-label="Aumenta quantità"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                data-testid={`add-to-cart-${pkg.slug}`}
                onClick={handleAdd}
                className="group flex flex-1 items-center justify-center gap-3 rounded-full bg-electric px-6 py-4 font-sans text-sm font-bold text-ink transition-all duration-300 hover:bg-glow hover:shadow-[0_0_36px_rgba(56,189,248,0.35)]"
              >
                Aggiungi al carrello
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>

            <div className="mt-8 grid gap-3 text-xs text-slate-400 sm:grid-cols-3">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-electric" /> Pagamento Stripe</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-electric" /> Kick-off in 5 giorni</span>
              <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-electric" /> Referente dedicato</span>
            </div>
          </motion.div>
        </div>

        <div className="mt-20 rounded-3xl border border-lineblue bg-panel p-8 sm:p-12">
          <Eyebrow>Cosa è incluso</Eyebrow>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {pkg.features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`detail-feature-${i}`}
                className="flex items-start gap-4"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cobalt/20 text-electric">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <p className="text-sm leading-relaxed text-slate-200">{f}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24 pb-24">
            <Eyebrow>Continua a esplorare</Eyebrow>
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {related.map((p, i) => (
                <PackageCard key={p.slug} pkg={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DettaglioPacchetto;
